/**
 * FluencyScreen - K Comparison Fluency Practice
 *
 * Timed fluency practice with 6-second per-question limit.
 * Plays TTS instruction, tracks scores, updates backend status to fluency3Practice when mastered.
 * Timer starts after TTS finishes.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { ComparisonFact } from './types';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../../hooks/useKAudio';
import { useAuth } from '../../../context/AuthContext';
import { getAccommodationTimerDuration, isAccommodationTimerUntimed } from '../../../policies/studentAccommodationPolicy';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';
import { progressQueue } from '../../../services/ProgressQueueService';

// Use PreK shared components
import {
  PracticeHeader,
  PRACTICE_PHASES,
} from '../../PreKPractice/shared';

// Fluency constants
const FLUENCY_TIME_LIMIT_MS = 6000; // 6 seconds per question

interface FluencyScreenProps {
  settings: KPracticeSettings;
  factsToPractice: ComparisonFact[];
  onComplete: () => void;
  onBack: () => void;
}

const FluencyScreen: React.FC<FluencyScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const { user } = useAuth();
  const timerDurationMs = getAccommodationTimerDuration(FLUENCY_TIME_LIMIT_MS, user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const isUntimed = isAccommodationTimerUntimed(user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const [currentFact, setCurrentFact] = useState<ComparisonFact | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());
  const factScoresRef = useRef(factScores);

  const [hasAnswered, setHasAnswered] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);

  // Hidden timer state
  const [timeRemaining, setTimeRemaining] = useState(timerDurationMs);
  const [timerActive, setTimerActive] = useState(false);

  // Timer tracking for attempt data
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const isFirstQuestionRef = useRef(true);

  const { playChime, playTTSWithCallback, isTTSPlaying } = useKTTS();

  // Reset timer helper function
  const resetTimer = useCallback((shouldStartTimer: boolean = false) => {
    setTimeRemaining(timerDurationMs);
    setTimerActive(shouldStartTimer && !isUntimed);
    setQuestionStartTime(Date.now());
  }, [timerDurationMs, isUntimed]);

  // Keep factScoresRef in sync with factScores state
  useEffect(() => {
    factScoresRef.current = factScores;
  }, [factScores]);

  // Initialize first fact when component loads
  useEffect(() => {
    if (!currentFact && factsToPractice.length > 0) {
      setCurrentFact(factsToPractice[0]);
    }
  }, [currentFact, factsToPractice]);

  // Reset state when fact changes
  useEffect(() => {
    if (currentFact) {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      setHasAnswered(false);
      setWrongAnswer(null);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      resetTimer(false);
    }
  }, [currentFact?.factId, resetTimer]);

  // Play TTS instruction (first question only)
  useEffect(() => {
    if (!ttsHasPlayed && currentFact) {
      if (isFirstQuestionRef.current && settings.speechEnabled) {
        // First question: play TTS instruction then start timer
        playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.comparing.weDoIntro, () => {
          setTtsHasPlayed(true);
          resetTimer(true);
        }).catch(() => {
          setTtsHasPlayed(true);
          resetTimer(true);
        });
      } else {
        // Subsequent questions or speech disabled: skip TTS, start timer immediately
        setTtsHasPlayed(true);
        resetTimer(true);
      }
    }
  }, [currentFact?.factId, settings.speechEnabled, playTTSWithCallback, ttsHasPlayed, resetTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  // Hidden 6-second countdown timer
  useEffect(() => {
    if (isUntimed || !timerActive || hasAnswered || timeRemaining <= 0) {
      if (timeRemaining <= 0 && !hasAnswered && currentFact) {
        // Time expired - treat as incorrect answer
        setHasAnswered(true);
        setTimerActive(false);

        // Calculate response time (full timer duration)
        const responseTime = Date.now() - questionStartTime;

        // Report timeout attempt data to progress queue
        if (user?.userId && currentFact) {
          try {
            progressQueue.enqueueProgressUpdate(user.userId, {
              facts: {
                [currentFact.factId]: {
                  attempts: 1,
                  correct: 0,
                  timeSpent: responseTime,
                },
              },
            });
          } catch (error) {
            console.error('Error reporting K comparison fluency timeout:', error);
          }
        }

        // Update fact score: -1 for incorrect
        setFactScores(prev => {
          const newScores = new Map(prev);
          const currentScore = newScores.get(currentFact.factId) || 0;
          const newScore = currentScore - 1;
          newScores.set(currentFact.factId, newScore);

          // If score goes below 1, decrement counter
          if (currentScore >= 1 && newScore < 1) {
            setCompletedCount(prevCount => prevCount - 1);
          }

          return newScores;
        });

        // Reset to allow another attempt after feedback
        const timeoutId = setTimeout(() => {
          setHasAnswered(false);
          setWrongAnswer(null);
          resetTimer(true);
        }, 1500);
        timeoutsRef.current.push(timeoutId);
      }
      return;
    }

    const timer = setTimeout(() => {
      setTimeRemaining(prev => prev - 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [isUntimed, timeRemaining, timerActive, hasAnswered, currentFact, user, questionStartTime, resetTimer]);

  const advanceToNext = useCallback(() => {
    // Check if all facts have score >= 1
    const latestScores = factScoresRef.current;
    const eligibleFacts = factsToPractice.filter(fact => {
      const score = latestScores.get(fact.factId) || 0;
      return score < 1;
    });

    if (eligibleFacts.length === 0) {
      // All facts completed
      onComplete();
      return;
    }

    // Reset answer state for new question (must reset here in case same fact is picked)
    isFirstQuestionRef.current = false;
    setHasAnswered(false);
    setWrongAnswer(null);
    setTtsHasPlayed(false);
    setCorrectAnswerGiven(false);
    resetTimer(false);

    // Select a new random fact from eligible facts
    const randomIndex = Math.floor(Math.random() * eligibleFacts.length);
    setCurrentFact(eligibleFacts[randomIndex]);
  }, [factsToPractice, onComplete, resetTimer]);

  const handleAnswerSubmission = useCallback((isCorrect: boolean, selectedNumber?: number) => {
    if (!currentFact) return;

    setHasAnswered(true);
    setTimerActive(false);

    // Calculate response time
    const responseTime = Date.now() - questionStartTime;

    // Report attempt data to progress queue
    if (user?.userId && currentFact) {
      try {
        progressQueue.enqueueProgressUpdate(user.userId, {
          facts: {
            [currentFact.factId]: {
              attempts: 1,
              correct: isCorrect ? 1 : 0,
              timeSpent: responseTime,
            },
          },
        });
      } catch (error) {
        console.error('Error reporting K comparison fluency attempt:', error);
      }
    }

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      // Update fact score: +1 for correct
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore + 1;
        newScores.set(currentFact.factId, newScore);

        // Only increment counter if going from 0 to 1 (first genuine correct)
        if (currentScore === 0 && newScore === 1) {
          setCompletedCount(prevCount => prevCount + 1);
        }

        // Update backend fact status to fluency3Practice when score reaches 1
        if (newScore === 1 && user?.userId) {
          const timeoutId = setTimeout(async () => {
            try {
              const progressUpdate: SetUserProgressRequest = {
                facts: {
                  [currentFact.factId]: {
                    status: 'fluency3Practice',
                  },
                },
              };

              await setUserProgress(user.userId, progressUpdate);
            } catch (error) {
              console.error('Failed to update fact status to fluency3Practice:', error);
            }
          }, 0);
          timeoutsRef.current.push(timeoutId);
        }

        return newScores;
      });

      // Play chime and advance after a delay
      playChime().then(() => {
        const timeoutId = setTimeout(() => {
          advanceToNext();
        }, 500);
        timeoutsRef.current.push(timeoutId);
      });
    } else {
      // Incorrect answer
      if (selectedNumber !== undefined) {
        setWrongAnswer(selectedNumber);
      }

      // Update fact score: -1 for incorrect
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore - 1;
        newScores.set(currentFact.factId, newScore);

        // If score goes below 1, decrement counter
        if (currentScore >= 1 && newScore < 1) {
          setCompletedCount(prevCount => prevCount - 1);
        }

        return newScores;
      });

      // Reset to allow another attempt after feedback
      const timeoutId = setTimeout(() => {
        setHasAnswered(false);
        setWrongAnswer(null);
        resetTimer(true);
      }, 1500);
      timeoutsRef.current.push(timeoutId);
    }
  }, [currentFact, playChime, advanceToNext, user, questionStartTime, resetTimer]);

  const handleAnswerClick = useCallback(
    (selectedNumber: number) => {
      // Don't allow clicks while TTS is playing or already answered
      if (isTTSPlaying || hasAnswered || !currentFact) return;

      const isCorrect = selectedNumber === currentFact.result;
      handleAnswerSubmission(isCorrect, selectedNumber);
    },
    [currentFact, hasAnswered, isTTSPlaying, handleAnswerSubmission]
  );

  if (!currentFact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.FLUENCY}
        onBack={onBack}
        progress={
          factsToPractice.length > 0
            ? Math.min(completedCount / factsToPractice.length, 1)
            : 0
        }
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md lg:max-w-lg">
          {/* Question prompt */}
          <div className="text-center mb-8">
            <div className="text-2xl lg:text-3xl text-gray-700 font-semibold">
              Which is greater?
            </div>
          </div>

          {/* Two number choices stacked vertically */}
          <div className="flex flex-col gap-6">
            {[currentFact.operand1, currentFact.operand2].map((num, index) => {
              const isCorrect = num === currentFact.result;
              const isWrong = wrongAnswer === num;
              const showAsCorrect = hasAnswered && isCorrect;

              return (
                <motion.button
                  key={`${currentFact.factId}-${index}`}
                  initial={isFirstQuestionRef.current ? { opacity: 0, y: 30 } : { opacity: 0 }}
                  animate={isFirstQuestionRef.current ? { opacity: 1, y: 0 } : { opacity: 1 }}
                  transition={isFirstQuestionRef.current ? { duration: 0.3, delay: index * 0.05 } : { duration: 0.15 }}
                  whileHover={!hasAnswered && !isTTSPlaying ? { scale: 1.05 } : {}}
                  whileTap={!hasAnswered && !isTTSPlaying ? { scale: 0.95 } : {}}
                  onClick={() => handleAnswerClick(num)}
                  disabled={hasAnswered || isTTSPlaying}
                  className={`
                    h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl
                    transition-all duration-300
                    ${
                      isWrong
                        ? 'bg-red-500 text-white border-4 border-red-700 animate-pulse'
                        : showAsCorrect
                        ? 'bg-green-500 text-white border-4 border-green-700'
                        : hasAnswered && !isCorrect
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400'
                        : isTTSPlaying
                        ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer'
                    }
                    ${hasAnswered || isTTSPlaying ? 'cursor-not-allowed' : 'hover:shadow-2xl'}
                  `}
                  style={NUMERAL_FONT_STYLE}
                >
                  {num}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FluencyScreen;
